import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ConversationTurn {
  speaker: 'user' | 'ai';
  message: string;
  delay?: number;
}

interface ExampleConversation {
  title: string;
  description: string;
  turns: ConversationTurn[];
}

interface AIDialogProps {
  isLoggedIn: boolean;
  title?: string;
  subtitle?: string;
  placeholder?: string;
  width?: string;
  onSendMessage?: (message: string) => Promise<string>;
  exampleQuestions?: string[];
  exampleConversations?: ExampleConversation[];
}

const AIDialog: React.FC<AIDialogProps> = ({
  isLoggedIn,
  title = "AI Assistant",
  subtitle = "Ask me anything",
  placeholder = "Type your message...",
  width = "350px",
  onSendMessage,
  exampleQuestions = [],
  exampleConversations = []
}) => {
  const navigate = useNavigate();
  
  // AI Dialog state
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [tooltipPosition, setTooltipPosition] = useState<{top: number, left: number}>({top: 0, left: 0});
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  const [messages, setMessages] = useState<Array<Message>>([]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimer) {
        clearTimeout(tooltipTimer);
      }
    };
  }, [tooltipTimer]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  }, [messages]);

  // AI Dialog functions
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      isUser: true,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    
    try {
      if (onSendMessage) {
        // Use custom message handler if provided
        const response = await onSendMessage(inputMessage);
        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: response,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);
      } else {
        // Default simulated response
        setTimeout(() => {
          const aiMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: `I understand you're asking about: "${inputMessage}". This is a simulated response. In a real implementation, this would connect to your AI backend to provide insights.`,
            isUser: false,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, aiMessage]);
          setIsLoading(false);
        }, 1500);
        return; // Don't set loading to false here since setTimeout handles it
      }
    } catch (error) {
      // Handle error
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "Sorry, I encountered an error. Please try again.",
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleExampleClick = (question: string) => {
    setInputMessage(question);
    // Auto-send the example question
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const handleExampleConversationClick = (conversation: ExampleConversation) => {
    // Clear existing messages
    setMessages([]);
    
    // Play through all conversation turns with delays
    conversation.turns.forEach((turn, index) => {
      setTimeout(() => {
        const message: Message = {
          id: (Date.now() + index).toString(),
          text: turn.message,
          isUser: turn.speaker === 'user',
          timestamp: new Date()
        };
        
        setMessages(prev => [...prev, message]);
      }, (turn.delay || 1000) * index);
    });
  };

  return (
    <>
      {/* Custom Scrollbar Styles */}
      <style>
        {`
          .ai-dialog-messages::-webkit-scrollbar {
            width: 8px;
          }
          
          .ai-dialog-messages::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          
          .ai-dialog-messages::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
            transition: background 0.2s ease;
          }
          
          .ai-dialog-messages::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          
          .ai-dialog-messages::-webkit-scrollbar-thumb:active {
            background: #64748b;
          }
        `}
      </style>
      
      {/* AI Dialog Interface */}
      <div style={{
        width: width,
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        opacity: isLoggedIn ? 1 : 0.7,
        position: 'relative'
      }}
      onMouseEnter={(e) => {
        if (!isLoggedIn) {
          // Clear any existing timer
          if (tooltipTimer) {
            clearTimeout(tooltipTimer);
          }
          
          // Calculate tooltip position
          const rect = e.currentTarget.getBoundingClientRect();
          const tooltipWidth = 200;
          let left = rect.left + rect.width / 2;
          
          // Ensure tooltip doesn't go off-screen
          if (left + tooltipWidth / 2 > window.innerWidth) {
            left = window.innerWidth - tooltipWidth / 2 - 10;
          } else if (left - tooltipWidth / 2 < 0) {
            left = tooltipWidth / 2 + 10;
          }
          
          setTooltipPosition({
            top: rect.bottom + 20,
            left: left
          });
          
          setShowTooltip(true);
          
          // Start 2-second timer to hide tooltip
          const timer = setTimeout(() => {
            setShowTooltip(false);
            setTooltipTimer(null);
          }, 2000);
          setTooltipTimer(timer);
        }
      }}
      onClick={() => {
        if (!isLoggedIn && showTooltip) {
          navigate('/login');
        }
      }}
      >
        {/* Dialog Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          background: isLoggedIn ? '#f8fafc' : '#f1f5f9'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: isLoggedIn ? '#10b981' : '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              🤖
            </div>
            <div>
              <h3 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '600',
                color: isLoggedIn ? '#1f2937' : '#6b7280'
              }}>
                {title}
              </h3>
              <p style={{
                margin: '2px 0 0 0',
                fontSize: '12px',
                color: isLoggedIn ? '#6b7280' : '#9ca3af'
              }}>
                {isLoggedIn ? subtitle : 'Sign in to access AI features'}
              </p>
            </div>
          </div>
        </div>

        {/* Messages Container */}
        <div 
          className="ai-dialog-messages"
          style={{
            flex: 1,
            padding: '20px',
            overflowY: 'auto',
            background: isLoggedIn ? '#ffffff' : '#f9fafb',
            maxHeight: '400px',
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 #f1f5f9'
          }}
        >
          {isLoggedIn ? (
            <>
              {messages.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '14px',
                  marginTop: '20px'
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>💬</div>
                  <p style={{ marginBottom: '20px' }}>Ask me anything about {title.toLowerCase()}!</p>
                  
                  {/* Example Conversations */}
                  {exampleConversations.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#9ca3af', 
                        marginBottom: '12px',
                        fontWeight: '500'
                      }}>
                        Try these tutorial conversations:
                      </p>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        alignItems: 'center'
                      }}>
                        {exampleConversations.map((conversation, index) => (
                          <div key={index} style={{ width: '100%', maxWidth: '300px' }}>
                            <button
                              onClick={() => handleExampleConversationClick(conversation)}
                              style={{
                                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                border: '1px solid #cbd5e1',
                                borderRadius: '12px',
                                padding: '12px 16px',
                                fontSize: '12px',
                                color: '#374151',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                textAlign: 'left',
                                width: '100%',
                                lineHeight: '1.4',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.borderColor = '#3b82f6';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)';
                                e.currentTarget.style.color = '#374151';
                                e.currentTarget.style.borderColor = '#cbd5e1';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                              }}
                            >
                              <div style={{ fontWeight: '600', marginBottom: '4px', color: 'inherit' }}>
                                🎯 {conversation.title}
                              </div>
                              <div style={{ fontSize: '11px', opacity: 0.8, color: 'inherit' }}>
                                {conversation.description}
                              </div>
                              <div style={{ fontSize: '10px', marginTop: '6px', opacity: 0.7, color: 'inherit' }}>
                                {conversation.turns.length} turns • Click to start tutorial
                              </div>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fallback Example Questions */}
                  {exampleQuestions.length > 0 && exampleConversations.length === 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <p style={{ 
                        fontSize: '12px', 
                        color: '#9ca3af', 
                        marginBottom: '12px',
                        fontWeight: '500'
                      }}>
                        Try asking:
                      </p>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        alignItems: 'center'
                      }}>
                        {exampleQuestions.map((question, index) => (
                          <button
                            key={index}
                            onClick={() => handleExampleClick(question)}
                            style={{
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '12px',
                              padding: '8px 12px',
                              fontSize: '12px',
                              color: '#475569',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              textAlign: 'left',
                              width: '100%',
                              maxWidth: '280px',
                              lineHeight: '1.4'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#3b82f6';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.borderColor = '#3b82f6';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#f8fafc';
                              e.currentTarget.style.color = '#475569';
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }}
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: message.isUser ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div style={{
                    maxWidth: '80%',
                    padding: '12px 16px',
                    borderRadius: '18px',
                    background: message.isUser 
                      ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                      : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    color: message.isUser ? 'white' : '#374151',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    border: message.isUser 
                      ? '1px solid #2563eb' 
                      : '1px solid #cbd5e1',
                    boxShadow: message.isUser 
                      ? '0 2px 8px rgba(59, 130, 246, 0.2)' 
                      : '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    {message.text}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '18px',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                    color: '#374151',
                    fontSize: '14px',
                    border: '1px solid #cbd5e1',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#3b82f6',
                        animation: 'pulse 1.5s infinite'
                      }} />
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#3b82f6',
                        animation: 'pulse 1.5s infinite 0.2s'
                      }} />
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: '#3b82f6',
                        animation: 'pulse 1.5s infinite 0.4s'
                      }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} style={{ paddingBottom: '10px' }} />
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '14px',
              marginTop: '40px',
              padding: '20px'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
              <p>Sign in to access the {title}</p>
              <p style={{ fontSize: '12px', marginTop: '8px' }}>
                Get insights about {title.toLowerCase()}
              </p>
            </div>
          )}
        </div>

        {/* Input Area */}
        {isLoggedIn && (
          <div style={{
            padding: '20px',
            borderTop: '1px solid #e5e7eb',
            background: '#ffffff'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end'
            }}>
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                style={{
                  flex: 1,
                  minHeight: '40px',
                  maxHeight: '120px',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '20px',
                  fontSize: '14px',
                  resize: 'none',
                  outline: 'none',
                  fontFamily: 'inherit'
                }}
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: 'none',
                  background: inputMessage.trim() && !isLoading ? '#3b82f6' : '#d1d5db',
                  color: 'white',
                  cursor: inputMessage.trim() && !isLoading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  transition: 'all 0.2s ease'
                }}
              >
                ➤
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tooltip for unauthenticated users */}
      {!isLoggedIn && showTooltip && (
        <div 
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateX(-50%)',
            backgroundColor: '#1e293b',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            lineHeight: '1.3',
            maxWidth: '200px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 9999,
            marginTop: '6px',
            border: '1px solid #334155',
            pointerEvents: 'none'
          }}
        >
          <div style={{ 
            marginBottom: '6px', 
            fontSize: '0.7rem', 
            opacity: 0.9
          }}>
            🔒 Sign up and subscribe to use this feature
          </div>
          <div style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '0.7rem',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            Sign In
          </div>
          
          {/* Tooltip arrow */}
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderBottom: '4px solid #1e293b'
          }} />
        </div>
      )}
    </>
  );
};

export default AIDialog;
